import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  Chip,
  Avatar,
  CircularProgress,
  alpha,
  keyframes
} from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  Chat as ChatIcon,
  FiberManualRecord as LiveIcon,
  Wifi as WifiIcon,
  Leaderboard as LeaderboardIcon,
  DirectionsCar as CarIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import { liveService } from '../services/live';
import { chatService } from '../services/chat';

const LiveDisplay = () => {
  const [displayData, setDisplayData] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Pulse animation for LIVE indicator
  const pulseAnimation = keyframes`
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.1); }
  `;

  // Load initial data
  useEffect(() => {
    loadDisplayData();
    loadChatHistory();
    connectWebSocket();

    // Refresh data periodically as backup
    const interval = setInterval(() => {
      loadDisplayData();
    }, 30000); // Every 30 seconds

    return () => {
      clearInterval(interval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const loadDisplayData = async () => {
    const result = await liveService.getLiveDisplayData();
    if (result.success) {
      setDisplayData(result.data);
      setError('');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const loadChatHistory = async () => {
    const result = await chatService.getChatHistory();
    if (result.success) {
      // Get last 50 messages
      setChatMessages(result.data.slice(-50));
    }
  };

  const connectWebSocket = () => {
    const ws = liveService.connectLiveWebSocket(
      (data) => {
        console.log('Live update received:', data);
        
        if (data.type === 'connected') {
          console.log('Connected to live display');
          setWsConnected(true);
        } else if (data.type === 'live_update') {
          // Reload data when updates occur
          loadDisplayData();
        } else if (data.type === 'message') {
          // Chat message received
          setChatMessages(prev => [...prev.slice(-49), data]);
        }
      },
      (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      }
    );

    wsRef.current = ws;
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh', 
        backgroundColor: '#14110E',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'transparent',
        }
      }}>
        <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <CircularProgress 
            size={80} 
            thickness={4}
            sx={{ 
              color: '#C9A227',
              mb: 2,
            }} 
          />
          <Typography variant="h5" sx={{ 
            color: '#F3EDE2',
            fontFamily: '"Fraunces", serif',
          }}>
            Loading Live Display...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh', 
        backgroundColor: '#14110E',
        p: 3 
      }}>
        <Paper sx={{
          p: 4,
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 3,
          textAlign: 'center'
        }}>
          <Typography variant="h5" sx={{ color: '#ef4444', mb: 2 }}>
            {error}
          </Typography>
          <Typography variant="body2" sx={{ color: '#aaa' }}>
            Please check your connection and try again
          </Typography>
        </Paper>
      </Box>
    );
  }

  const config = displayData?.config;
  const layout = config?.layout_config || {};

  return (
    <Box sx={{ 
      height: '100vh', 
      backgroundColor: '#14110E',
      color: '#fff',
      p: { xs: 1, md: 1.5 },
      overflow: 'hidden',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'transparent',
        pointerEvents: 'none',
        zIndex: 0
      }
    }}>
      <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Grid container spacing={1.5} sx={{ flex: 1, overflow: 'hidden' }}>
          {/* Compact Event Name Header */}
          {layout.event_name?.enabled && (
            <Grid item xs={12}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 1.5, 
                  textAlign: 'center', 
                  backgroundColor: '#241E18',
                  border: '1px solid rgba(201, 162, 39, 0.3)',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    icon={<LiveIcon />}
                    label="LIVE"
                    size="small"
                    sx={{
                      bgcolor: alpha('#ef4444', 0.2),
                      color: '#ef4444',
                      border: '1px solid #ef4444',
                      fontWeight: 700,
                      animation: `${pulseAnimation} 2s ease-in-out infinite`,
                      '& .MuiChip-icon': {
                        color: '#ef4444',
                        animation: `${pulseAnimation} 2s ease-in-out infinite`
                      }
                    }}
                  />
                  {wsConnected && (
                    <Chip 
                      icon={<WifiIcon />}
                      label="Connected"
                      size="small"
                      sx={{
                        bgcolor: alpha('#6B8F71', 0.2),
                        color: '#6B8F71',
                        border: '1px solid #6B8F71',
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: '#6B8F71' }
                      }}
                    />
                  )}
                </Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 800,
                    color: '#F3EDE2', fontFamily: '"Fraunces", serif',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {config?.event_name || 'OC-LANgenthal Live Display'}
                </Typography>
                <Box sx={{ width: 140 }} /> {/* Spacer for balance */}
              </Paper>
            </Grid>
          )}

        {/* Top Row: Video Feed + Live Chat */}
        {/* Video Feed */}
        {layout.video_feed?.enabled && config?.video_stream_url && (
          <Grid item xs={12} md={8}>
            <Paper 
              elevation={0}
              sx={{ 
                height: '60vh', 
                backgroundColor: '#241E18',
                border: '2px solid rgba(201, 162, 39, 0.3)',
                borderRadius: 3, 
                overflow: 'hidden',
                position: 'relative',
                transition: 'all 0.3s ease',
                '&:hover': {
                  border: '2px solid rgba(61, 122, 140, 0.5)',
                }
              }}
            >
              {/* LIVE Indicator Badge */}
              <Box sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 10,
                display: 'flex',
                gap: 1
              }}>
                <Chip
                  icon={<LiveIcon />}
                  label="LIVE STREAM"
                  sx={{
                    bgcolor: alpha('#ef4444', 0.9),
                    color: '#fff',
                    fontWeight: 700,
                    animation: `${pulseAnimation} 2s ease-in-out infinite`,
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                    '& .MuiChip-icon': {
                      color: '#fff',
                      animation: `${pulseAnimation} 2s ease-in-out infinite`
                    }
                  }}
                />
              </Box>

              {config.video_stream_url ? (
                <iframe
                  src={config.video_stream_url}
                  title="Live Stream"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    display: 'block'
                  }}
                  allowFullScreen
                />
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(20, 17, 14, 0.8) 0%, rgba(36, 30, 24, 0.8) 100%)',
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <LiveIcon sx={{ 
                      fontSize: 64, 
                      color: alpha('#C9A227', 0.5), 
                      mb: 2,
                      animation: `${pulseAnimation} 2s ease-in-out infinite` 
                    }} />
                    <Typography variant="h6" sx={{ color: alpha('#fff', 0.7) }}>
                      No video stream configured
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        )}

        {/* Live Chat */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={0}
            sx={{ 
              height: '60vh',
              p: 2, 
              backgroundColor: '#241E18',
              border: '1px solid rgba(61, 122, 140, 0.3)',
              borderRadius: 2, 
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ChatIcon sx={{ color: '#3D7A8C', fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
                Live Chat
              </Typography>
              <Chip
                icon={<LiveIcon />}
                label="LIVE"
                size="small"
                sx={{
                  ml: 'auto',
                  bgcolor: alpha('#6B8F71', 0.2),
                  color: '#6B8F71',
                  border: '1px solid #6B8F71',
                  fontWeight: 700,
                  animation: `${pulseAnimation} 2s ease-in-out infinite`,
                  '& .MuiChip-icon': { 
                    color: '#6B8F71',
                    animation: `${pulseAnimation} 2s ease-in-out infinite`
                  }
                }}
              />
            </Box>
            <Box 
              ref={chatContainerRef}
              sx={{ 
                flexGrow: 1,
                overflowY: 'auto',
                pr: 1,
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-track': {
                  background: alpha('#fff', 0.05),
                  borderRadius: 3
                },
                '&::-webkit-scrollbar-thumb': { 
                  backgroundColor: '#3D3428',
                  borderRadius: 3
                }
              }}
            >
              {chatMessages.map((msg, index) => (
                <Box 
                  key={msg._id || index} 
                  sx={{ 
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    background: alpha('#fff', 0.03),
                    border: `1px solid ${alpha('#fff', 0.08)}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha('#fff', 0.05),
                      transform: 'translateX(4px)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Avatar 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        backgroundColor: '#C9A227',
                        color: '#14110E',
                        fontSize: 12,
                        fontWeight: 700
                      }}
                    >
                      {msg.username?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        color: '#C9A227', 
                        fontWeight: 700
                      }}
                    >
                      {msg.username}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: alpha('#fff', 0.4),
                        ml: 'auto'
                      }}
                    >
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: alpha('#fff', 0.9), 
                      ml: 4
                    }}
                  >
                    {msg.message}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Bottom Row: ACC Rankings, Trackmania Rankings, Bar Stats, Gulag Rankings */}
        {/* ACC Time Trial Rankings */}
        <Grid item xs={12} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              height: '25vh',
              p: 1.5, 
              background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.1) 0%, rgba(239, 68, 68, 0.1) 100%)',
              border: '1px solid rgba(251, 146, 60, 0.3)',
              borderRadius: 2, 
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <CarIcon sx={{ color: '#FB923C', fontSize: 16 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.85rem' }}>
                ACC Standings
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              {displayData?.acc_time_trial_rankings && displayData.acc_time_trial_rankings.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {displayData.acc_time_trial_rankings.slice(0, 5).map((driver) => {
                    const position = driver.position;
                    const colors = { 
                      1: { bg: '#C9A227', text: '#C9A227' }, 
                      2: { bg: '#C0C0C0', text: '#C0C0C0' }, 
                      3: { bg: '#CD7F32', text: '#CD7F32' }, 
                      4: { bg: '#FB923C', text: '#FB923C' }, 
                      5: { bg: '#EF4444', text: '#EF4444' } 
                    };
                    const color = colors[position] || { bg: '#FB923C', text: '#FB923C' };
                    return (
                      <Box key={driver.driver_name} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 0.75, borderRadius: 1, background: alpha(color.bg, 0.05), border: `1px solid ${alpha(color.bg, 0.3)}` }}>
                        <Box sx={{ width: 24, height: 24, borderRadius: 0.5, background: alpha(color.bg, 0.2), border: `1px solid ${color.bg}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.75rem', color: color.text }}>{position}</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>{driver.username || driver.driver_name}</Typography>
                          <Typography sx={{ color: alpha('#fff', 0.5), fontSize: '0.65rem', lineHeight: 1.2 }}>
                            {driver.track_count} tracks
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ color: color.text, fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.2 }}>
                            {Math.round(driver.total_points)} pts
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.4), textAlign: 'center', display: 'block', mt: 4 }}>
                  No ACC rankings available yet
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Trackmania Time Trial Rankings */}
        <Grid item xs={12} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              height: '25vh',
              p: 1.5, 
              background: 'linear-gradient(135deg, rgba(107, 143, 113, 0.1) 0%, rgba(61, 122, 140, 0.1) 100%)',
              border: '1px solid rgba(107, 143, 113, 0.3)',
              borderRadius: 2, 
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <TimerIcon sx={{ color: '#6B8F71', fontSize: 16 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.85rem' }}>
                Trackmania Standings
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              {displayData?.trackmania_time_trial_rankings && displayData.trackmania_time_trial_rankings.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {displayData.trackmania_time_trial_rankings.slice(0, 5).map((player) => {
                    const position = player.position;
                    const colors = { 
                      1: { bg: '#C9A227', text: '#C9A227' }, 
                      2: { bg: '#C0C0C0', text: '#C0C0C0' }, 
                      3: { bg: '#CD7F32', text: '#CD7F32' }, 
                      4: { bg: '#6B8F71', text: '#6B8F71' }, 
                      5: { bg: '#3D7A8C', text: '#3D7A8C' } 
                    };
                    const color = colors[position] || { bg: '#6B8F71', text: '#6B8F71' };
                    return (
                      <Box key={player.player_nickname} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 0.75, borderRadius: 1, background: alpha(color.bg, 0.05), border: `1px solid ${alpha(color.bg, 0.3)}` }}>
                        <Box sx={{ width: 24, height: 24, borderRadius: 0.5, background: alpha(color.bg, 0.2), border: `1px solid ${color.bg}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.75rem', color: color.text }}>{position}</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>{player.username || player.player_nickname}</Typography>
                          <Typography sx={{ color: alpha('#fff', 0.5), fontSize: '0.65rem', lineHeight: 1.2 }}>
                            {player.map_count} maps
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ color: color.text, fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.2 }}>
                            {Math.round(player.total_points)} pts
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.4), textAlign: 'center', display: 'block', mt: 4 }}>
                  No Trackmania rankings available yet
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Bar Stats */}
        <Grid item xs={12} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              height: '25vh',
              p: 1.5, 
              background: 'linear-gradient(135deg, rgba(107, 143, 113, 0.1) 0%, rgba(61, 122, 140, 0.1) 100%)',
              border: '1px solid rgba(107, 143, 113, 0.3)',
              borderRadius: 2, 
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <RestaurantIcon sx={{ color: '#6B8F71', fontSize: 16 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.85rem' }}>
                Bar Statistics
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              {displayData?.bar_stats ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ p: 0.75, borderRadius: 1, background: alpha('#fff', 0.03), border: `1px solid ${alpha('#6B8F71', 0.2)}` }}>
                    <Typography sx={{ color: alpha('#fff', 0.6), fontSize: '0.65rem', mb: 0.25 }}>Total Amount Spent</Typography>
                    <Typography sx={{ color: '#6B8F71', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>CHF {displayData.bar_stats.total_revenue.toFixed(2)}</Typography>
                    <Typography sx={{ color: alpha('#fff', 0.4), fontSize: '0.6rem', lineHeight: 1.2 }}>{displayData.bar_stats.total_transactions} transactions</Typography>
                  </Box>
                  {displayData.bar_stats.most_popular_drink && (
                    <Box sx={{ p: 0.75, borderRadius: 1, background: alpha('#3D7A8C', 0.05), border: `1px solid ${alpha('#3D7A8C', 0.3)}` }}>
                      <Typography sx={{ color: alpha('#fff', 0.6), fontSize: '0.65rem', mb: 0.25 }}>Most Favorite Drink</Typography>
                      <Typography sx={{ color: '#3D7A8C', fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.2 }}>{displayData.bar_stats.most_popular_drink}</Typography>
                      <Typography sx={{ color: alpha('#fff', 0.5), fontSize: '0.6rem', lineHeight: 1.2 }}>{displayData.bar_stats.most_popular_drink_count} ordered</Typography>
                    </Box>
                  )}
                  {displayData.bar_stats.highest_spender && (
                    <Box sx={{ p: 0.75, borderRadius: 1, background: alpha('#C9A227', 0.05), border: `1px solid ${alpha('#C9A227', 0.3)}` }}>
                      <Typography sx={{ color: alpha('#fff', 0.6), fontSize: '0.65rem', mb: 0.25 }}>Highest Spender</Typography>
                      <Typography sx={{ color: '#C9A227', fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.2 }}>{displayData.bar_stats.highest_spender.username}</Typography>
                      <Typography sx={{ color: '#6B8F71', fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.2 }}>CHF {displayData.bar_stats.highest_spender.amount.toFixed(2)}</Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.4), textAlign: 'center', display: 'block' }}>No bar activity yet</Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Gulag Rankings */}
        <Grid item xs={12} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              height: '25vh',
              p: 1.5, 
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 193, 7, 0.1) 100%)',
              border: '1px solid rgba(255, 215, 0, 0.3)',
              borderRadius: 2, 
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <LeaderboardIcon sx={{ color: '#C9A227', fontSize: 16 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.85rem' }}>
                Gulag Rankings
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              {displayData?.rankings && displayData.rankings.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {displayData.rankings.slice(0, 5).map((ranking) => {
                    const colors = { 1: { bg: '#C9A227', text: '#C9A227' }, 2: { bg: '#C0C0C0', text: '#C0C0C0' }, 3: { bg: '#CD7F32', text: '#CD7F32' }, 4: { bg: '#A89B8C', text: '#A89B8C' }, 5: { bg: '#A89B8C', text: '#A89B8C' } };
                    const color = colors[ranking.rank] || { bg: '#A89B8C', text: '#A89B8C' };
                    return (
                      <Box key={ranking.rank} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 0.75, borderRadius: 1, background: alpha(color.bg, 0.05), border: `1px solid ${alpha(color.bg, 0.3)}` }}>
                        <Box sx={{ width: 24, height: 24, borderRadius: 0.5, background: alpha(color.bg, 0.2), border: `1px solid ${color.bg}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.75rem', color: color.text }}>{ranking.rank}</Box>
                        <Typography sx={{ color: '#fff', fontWeight: 600, flex: 1, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>{ranking.username}</Typography>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ color: color.text, fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.2 }}>{Math.round(ranking.points)}</Typography>
                          <Typography sx={{ color: alpha('#fff', 0.5), fontSize: '0.65rem', lineHeight: 1.2 }}>
                            {ranking.wins}W-{ranking.losses}L
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.4), textAlign: 'center', display: 'block', mt: 4 }}>
                  No rankings available yet
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Time Trial Leaderboards - Show for each active trial */}
        {displayData?.active_leaderboards && displayData.active_leaderboards.length > 0 && (
          displayData.active_leaderboards.map((leaderboard) => (
            <Grid item xs={12} md={4} key={leaderboard.trial_id}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 1.5, 
                  background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 193, 7, 0.1) 100%)',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  borderRadius: 2, 
                  height: 'calc(50vh - 60px)',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <LeaderboardIcon sx={{ color: '#C9A227', fontSize: 18 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.75rem' }}>
                    {leaderboard.trial_name}
                  </Typography>
                  {leaderboard.is_finalized && (
                    <Chip
                      label="FINAL"
                      size="small"
                      sx={{
                        ml: 'auto',
                        bgcolor: alpha('#6B8F71', 0.2),
                        color: '#6B8F71',
                        border: '1px solid #6B8F71',
                        fontWeight: 700,
                        fontSize: '0.6rem',
                        height: 16
                      }}
                    />
                  )}
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 0.75,
                  overflowY: 'auto',
                  flexGrow: 1
                }}>
                  {leaderboard.standings && leaderboard.standings.length > 0 ? (
                    leaderboard.standings.map((entry) => {
                      const colors = {
                        1: { bg: '#C9A227', border: '#C9A227', text: '#C9A227' },
                        2: { bg: '#C0C0C0', border: '#C0C0C0', text: '#C0C0C0' },
                        3: { bg: '#CD7F32', border: '#CD7F32', text: '#CD7F32' }
                      };
                      const color = colors[entry.position] || { bg: '#3D7A8C', border: '#3D7A8C', text: '#3D7A8C' };
                      
                      return (
                        <Box 
                          key={`${leaderboard.trial_id}-${entry.position}`}
                          sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 0.75,
                            borderRadius: 1,
                            background: alpha(color.bg, 0.05),
                            border: `1px solid ${alpha(color.border, entry.position <= 3 ? 0.3 : 0.2)}`,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              background: alpha(color.bg, 0.1),
                              transform: 'translateX(2px)'
                            }
                          }}
                        >
                          <Box sx={{
                            width: 22,
                            height: 22,
                            borderRadius: 1,
                            background: alpha(color.bg, 0.2),
                            border: `1px solid ${color.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 800,
                            fontSize: '0.7rem',
                            color: color.text
                          }}>
                            {entry.position}
                          </Box>
                          <Avatar 
                            sx={{ 
                              width: 20, 
                              height: 20, 
                              background: `linear-gradient(135deg, ${color.bg} 0%, ${color.border} 100%)`,
                              fontSize: 9,
                              fontWeight: 700
                            }}
                          >
                            {entry.driver_name?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ 
                              color: '#fff', 
                              fontWeight: 600, 
                              fontSize: '0.75rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {entry.username || entry.driver_name}
                            </Typography>
                            {entry.username && entry.driver_name !== entry.username && (
                              <Typography sx={{ 
                                color: alpha('#fff', 0.4), 
                                fontSize: '0.6rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {entry.driver_name}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ color: color.text, fontWeight: 700, fontSize: '0.8rem' }}>
                              {entry.total_points}
                            </Typography>
                            <Typography sx={{ color: alpha('#fff', 0.5), fontSize: '0.6rem' }}>
                              pts
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    <Box sx={{ 
                      flexGrow: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2,
                      textAlign: 'center'
                    }}>
                      <Box>
                        <LeaderboardIcon sx={{ fontSize: 32, color: alpha('#C9A227', 0.3), mb: 0.5 }} />
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.4), fontSize: '0.7rem' }}>
                          No standings yet
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          ))
        )}

        {/* Sponsors - Bottom */}
        {layout.sponsors?.enabled && displayData?.sponsors && displayData.sponsors.length > 0 && (
          <Grid item xs={12}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 1.5, 
                backgroundColor: '#241E18',
                border: '1px solid rgba(201, 162, 39, 0.2)',
                borderRadius: 2
              }}
            >
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 1,
                  textAlign: 'center',
                  color: '#C9A227',
                  fontFamily: '"Barlow Condensed", sans-serif',
                }}
              >
                Proudly Sponsored By
              </Typography>
              <Grid container spacing={1} alignItems="center" justifyContent="center">
                {displayData.sponsors.map((sponsor) => (
                  <Grid item xs={4} sm={3} md={2} key={sponsor.id}>
                    <Card 
                      sx={{ 
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
                        border: '1px solid rgba(201, 162, 39, 0.1)',
                        textAlign: 'center',
                        p: 1,
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1.5,
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': { 
                          border: '1px solid rgba(201, 162, 39, 0.4)',
                        }
                      }}
                    >
                      {sponsor.logo_url ? (
                        <img
                          src={sponsor.logo_url}
                          alt={sponsor.name}
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: 45, 
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = `<div style="color: #666; font-weight: 600; font-size: 0.75rem;">${sponsor.name}</div>`;
                          }}
                        />
                      ) : (
                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem' }}>
                          {sponsor.name}
                        </Typography>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>
      </Box>
    </Box>
  );
};

export default LiveDisplay;
